import { FirebaseError } from 'firebase/app';
import { AuthErrorType, AuthError } from '../../types/user';

/**
 * Maps Firebase error codes to user-friendly error messages and categories
 */
const AUTH_ERROR_MAP: Record<string, { message: string; type: AuthErrorType; action?: string }> = {
  // Email/Password Authentication Errors
  'auth/email-already-in-use': {
    message: 'This email address is already in use by another account.',
    type: AuthErrorType.ACCOUNT,
    action: 'Try signing in instead or use a different email address.'
  },
  'auth/invalid-email': {
    message: 'The email address is not valid.',
    type: AuthErrorType.CREDENTIALS,
    action: 'Please enter a valid email address.'
  },
  'auth/user-disabled': {
    message: 'This account has been disabled.',
    type: AuthErrorType.ACCOUNT,
    action: 'Please contact support for assistance.'
  },
  'auth/user-not-found': {
    message: 'No account found with this email address.',
    type: AuthErrorType.CREDENTIALS,
    action: 'Please check your email or sign up for a new account.'
  },
  'auth/wrong-password': {
    message: 'Incorrect password.',
    type: AuthErrorType.CREDENTIALS,
    action: 'Please check your password or try resetting it.'
  },
  'auth/weak-password': {
    message: 'The password is too weak.',
    type: AuthErrorType.CREDENTIALS,
    action: 'Please use a stronger password with at least 6 characters.'
  },
  'auth/invalid-credential': {
    message: 'The login credentials are invalid.',
    type: AuthErrorType.CREDENTIALS,
    action: 'Please check your email and password.'
  },
  'auth/invalid-verification-code': {
    message: 'The verification code is invalid.',
    type: AuthErrorType.VERIFICATION,
    action: 'Please try requesting a new verification code.'
  },
  'auth/invalid-verification-id': {
    message: 'The verification ID is invalid.',
    type: AuthErrorType.VERIFICATION,
    action: 'Please try the verification process again.'
  },

  // Account Management Errors
  'auth/requires-recent-login': {
    message: 'This operation requires recent authentication.',
    type: AuthErrorType.VERIFICATION,
    action: 'Please sign in again to confirm this action.'
  },
  'auth/email-already-exists': {
    message: 'This email address already exists.',
    type: AuthErrorType.ACCOUNT,
    action: 'Please use a different email address.'
  },

  // Network Errors
  'auth/network-request-failed': {
    message: 'A network error has occurred.',
    type: AuthErrorType.NETWORK,
    action: 'Please check your internet connection and try again.'
  },
  'auth/timeout': {
    message: 'The operation has timed out.',
    type: AuthErrorType.NETWORK,
    action: 'Please try again later.'
  },

  // Social Authentication Errors
  'auth/account-exists-with-different-credential': {
    message: 'An account already exists with the same email address but different sign-in credentials.',
    type: AuthErrorType.ACCOUNT,
    action: 'Try signing in using the method you used previously.'
  },
  'auth/popup-blocked': {
    message: 'The sign-in popup was blocked by your browser.',
    type: AuthErrorType.NETWORK,
    action: 'Please allow popups for this site and try again.'
  },
  'auth/popup-closed-by-user': {
    message: 'The sign-in popup was closed before completing the sign-in process.',
    type: AuthErrorType.ACCOUNT,
    action: 'Please keep the popup open until the sign-in process is complete.'
  },
  'auth/cancelled-popup-request': {
    message: 'The sign-in request was cancelled.',
    type: AuthErrorType.ACCOUNT,
    action: 'Please try signing in again.'
  },

  // Claims and Token Errors
  'auth/id-token-expired': {
    message: 'Your authentication session has expired.',
    type: AuthErrorType.VERIFICATION,
    action: 'Please sign in again.'
  },
  'auth/id-token-revoked': {
    message: 'Your authentication token has been revoked for security reasons.',
    type: AuthErrorType.VERIFICATION,
    action: 'Please sign in again.'
  },

  // General Errors
  'auth/internal-error': {
    message: 'An internal error has occurred.',
    type: AuthErrorType.UNKNOWN,
    action: 'Please try again later.'
  },
  'auth/too-many-requests': {
    message: 'Too many failed login attempts. Access has been temporarily disabled.',
    type: AuthErrorType.PERMISSIONS,
    action: 'Please try again later or reset your password.'
  },
  'auth/operation-not-allowed': {
    message: 'This authentication method is not enabled.',
    type: AuthErrorType.PERMISSIONS,
    action: 'Please contact support or try a different sign-in method.'
  }
};

/**
 * Converts a Firebase error to a user-friendly error object
 * @param error Firebase error
 * @returns Formatted auth error with message, type, and action
 */
export const formatAuthError = (error: FirebaseError): AuthError => {
  const errorInfo = AUTH_ERROR_MAP[error.code] || {
    message: 'An authentication error occurred.',
    type: AuthErrorType.UNKNOWN,
    action: 'Please try again or contact support if the issue persists.'
  };

  return {
    code: error.code,
    message: errorInfo.message,
    type: errorInfo.type,
    action: errorInfo.action
  };
};

/**
 * Gets user-friendly error message for a Firebase error
 * @param error Firebase error
 * @returns User-friendly error message
 */
export const getAuthErrorMessage = (error: FirebaseError): string => {
  return formatAuthError(error).message;
};

/**
 * Gets recommended action for resolving a Firebase error
 * @param error Firebase error
 * @returns Recommended action or null if no action is available
 */
export const getAuthErrorAction = (error: FirebaseError): string | undefined => {
  return formatAuthError(error).action;
};

/**
 * Checks if a Firebase error is of a specific type
 * @param error Firebase error
 * @param type Error type to check
 * @returns True if error is of the specified type
 */
export const isAuthErrorOfType = (error: FirebaseError, type: AuthErrorType): boolean => {
  return formatAuthError(error).type === type;
};

/**
 * Determines if an error requires the user to reauthenticate
 * @param error Firebase error
 * @returns True if reauthentication is required
 */
export const requiresReauthentication = (error: FirebaseError): boolean => {
  return error.code === 'auth/requires-recent-login';
};
